import React, { ReactNode } from "react"
import { ApolloProvider, ApolloClient, InMemoryCache, createHttpLink, gql, ApolloLink } from "@apollo/client"
import { onError } from "@apollo/client/link/error"
import { setContext } from "@apollo/client/link/context"
// @ts-ignore
import LocalSchema from "../common/LocalSchema.graphql"
import LocalResolvers from "../common/LocalResolver"
import { APOLLO_SERVER_URL, APPLICATION_KO_ORGANIZATIO_ID } from "../../constants/constants"
import { getGlobalMaintenanceSetter } from "../../contexts/MaintenanceContext"
import { captureGraphQLError } from "../../helpers/sentry"
import { safeStorage } from "../../helpers/safeStorage"
import { stripReadonlyBookingFields } from "../../helpers/stripReadonlyBookingFields"
import { AUTH_SESSION_STORAGE_KEYS } from "../../helpers/authStorageKeys"
import { buildAuthHeaders, buildStaticHeaders } from "../../helpers/requestHeaders"

type ApolloWrapperProps = {
    children: ReactNode
}

const uri = APOLLO_SERVER_URL
// Phase 2: Kein Fallback-UUID mehr! Backend erkennt Tenant über Origin-Header.
// APPLICATION_KO_ORGANIZATIO_ID ist nur noch für lokale Entwicklung relevant (.env.development).
// Statische Header und Auth-Token kommen aus helpers/requestHeaders — dieselbe Quelle
// wie der Apollo-freie Meldepfad (services/reportEndUserEvent).

const cleanTypenameLink = new ApolloLink((operation, forward) => {
    if (operation.variables) {
        const cleanedVariables = JSON.parse(JSON.stringify(operation.variables), (key, value) =>
            key === "__typename" ? undefined : value
        )
        // JSON.parse(JSON.stringify(...)) liefert eine tiefe Kopie -> Mutation ist sicher.
        stripReadonlyBookingFields(cleanedVariables)
        operation.variables = cleanedVariables
    }
    return forward(operation)
})

const httpLink: any = createHttpLink({
    uri,
    fetch,
    headers: buildStaticHeaders(),
    credentials: "include",
})

// Link-Middleware für Logging
const loggingLink = new ApolloLink((operation, forward) => {
    const context = operation.getContext()
    const organizationId = context.headers?.["x-ko-organization-id"] || APPLICATION_KO_ORGANIZATIO_ID

    return forward(operation).map((response) => {
        if (response.errors) {
            console.error("GraphQL Errors:", {
                organizationId,
                errors: response.errors,
            })
            captureGraphQLError(response.errors, operation.operationName, operation.variables)
        }
        return response
    })
})

// Error Link für Maintenance-Erkennung
const maintenanceErrorLink = onError(({ graphQLErrors, networkError }) => {
    const setMaintenanceFromError = getGlobalMaintenanceSetter()

    // Prüfe auf GraphQL-Fehler die auf Maintenance hindeuten
    if (graphQLErrors) {
        for (const err of graphQLErrors) {
            const message = err.message || ""
            const code = (err.extensions?.code as string) || ""

            // Ignoriere Schema-Fehler (z.B. "Cannot query field") - das ist kein Maintenance
            if (message.includes("Cannot query field") || message.includes("Unknown field")) {
                continue
            }

            if (
                message.toLowerCase().includes("service unavailable") ||
                message.toLowerCase().includes("maintenance") ||
                message.toLowerCase().includes("wartung") ||
                code === "SERVICE_UNAVAILABLE" ||
                code === "MAINTENANCE"
            ) {
                console.log("[Maintenance] Maintenance detected from GraphQL error:", message)
                if (setMaintenanceFromError) {
                    setMaintenanceFromError(message)
                }
                return
            }
        }
    }

    // Prüfe auf Netzwerkfehler (z.B. 503 Service Unavailable)
    if (networkError) {
        const statusCode = (networkError as any).statusCode
        const message = networkError.message || ""

        if (
            statusCode === 503 ||
            message.toLowerCase().includes("service unavailable") ||
            message.toLowerCase().includes("maintenance")
        ) {
            console.log("[Maintenance] Maintenance detected from network error:", message)
            if (setMaintenanceFromError) {
                setMaintenanceFromError(message)
            }
        }
    }
})

// Hinweis: AUTH_SESSION_STORAGE_KEYS (helpers/authStorageKeys.ts) enthält bewusst
// WENIGER Keys als der reguläre Logout (LOGOUT_STORAGE_KEYS) — eine laufende Buchung
// soll einen Session-Ablauf überleben. Außerdem darf ein fehlgeschlagener Login-Versuch
// mit alten Token-Resten (Backend antwortet UNAUTHENTICATED) keine Buchungsdaten
// zerstören. Begründung der Divergenz: siehe Kommentar im Key-Modul.

// Error Link für abgelaufene/ungültige Tokens.
// Ohne dieses Handling bleibt der User scheinbar eingeloggt (loggedInUser im Storage),
// während jede Query scheitert — eine Sackgasse ohne Ausweg.
const authErrorLink = onError(({ graphQLErrors, networkError }) => {
    // Nur ECHTE Auth-Fehler erkennen: UNAUTHENTICATED (GraphQL) oder HTTP 401 (Netzwerk).
    // Bewusst KEIN Matching auf FORBIDDEN o.ä. — das sind Business-Fehler auf einzelnen
    // Feldern und dürfen nicht als Logout interpretiert werden.
    const hasUnauthenticatedError = (graphQLErrors ?? []).some((err) => (err.extensions?.code as string) === "UNAUTHENTICATED")
    const has401NetworkError = (networkError as any)?.statusCode === 401

    if (!hasUnauthenticatedError && !has401NetworkError) {
        return
    }

    // Loop-Schutz: nur auslösen, wenn laut Storage überhaupt eine Session existiert.
    // Anonyme Nutzer (z.B. Public-Query-Fallbacks wie PaymentReturn) bekommen ebenfalls
    // UNAUTHENTICATED — die dürfen hier nicht betroffen sein. Nach dem ersten Auslösen
    // sind die Keys entfernt, weitere Fehler derselben Welle laufen ins Leere.
    const hadAuthData = !!safeStorage.getItem("loggedInUser") || !!safeStorage.getItem("accessToken")
    if (!hadAuthData) {
        return
    }

    console.warn("[Auth] Sitzung abgelaufen oder ungültig — lokale Auth-Daten werden entfernt.")
    AUTH_SESSION_STORAGE_KEYS.forEach((key) => safeStorage.removeItem(key))
    // App.tsx lauscht auf dieses Event, aktualisiert isLoggedIn und leitet selbst
    // zum Login — bewusst KEIN harter Redirect/Reload an dieser Stelle.
    window.dispatchEvent(new Event("localStorageChange"))
})

// Statische Header je Request neu: Die Tenant-ID des Embeds steht erst nach dem Import fest
const authLink = setContext((_, { headers }) => ({
    headers: {
        ...headers,
        ...buildStaticHeaders(),
        ...buildAuthHeaders(),
    },
}))

const cache = new InMemoryCache({
    addTypename: false,
    dataIdFromObject: ({ id }) => (id ? String(id) : undefined),
})

// Initial cache data
cache.writeQuery({
    query: gql`
        query GetLocalState {
            LocalState @client {
                createCourse {
                    locationId
                }
                bookAttendees {
                    selectedAttendee {
                        id
                        firstname
                        bookedCourseIds
                    }
                    selectedCourseId
                }
                bookPaymentMethods {
                    selectedPaymentMethod {
                        id
                        type
                        bookedCourseIds
                    }
                    selectedCourseId
                }
            }
        }
    `,
    data: {
        LocalState: {
            createCourse: {
                locationId: null,
            },
            bookAttendees: {
                selectedAttendee: {
                    id: null,
                    firstname: null,
                    bookedCourseIds: null,
                },
                selectedCourseId: null,
            },
            bookPaymentMethods: {
                selectedPaymentMethod: {
                    id: null,
                    type: null,
                    bookedCourseIds: null,
                },
                selectedCourseId: null,
            },
        },
    },
})

// export const apolloClient = new ApolloClient({
//     link: authLink.concat(httpLink),
//     cache: new InMemoryCache(),
//     typeDefs: LocalSchema,
//     resolvers: LocalResolvers as any,
// })

// export const apolloClient = new ApolloClient({
//     link: ApolloLink.from([cleanTypenameLink, authLink, httpLink]),
//     cache: new InMemoryCache({
//         addTypename: false,
//         dataIdFromObject: ({ id }) => (id ? String(id) : undefined),
//     }),
//     typeDefs: LocalSchema,
//     resolvers: LocalResolvers as any,
// })

export const apolloClient = new ApolloClient({
    link: ApolloLink.from([maintenanceErrorLink, authErrorLink, cleanTypenameLink, authLink, loggingLink, httpLink]),
    cache: new InMemoryCache({
        addTypename: false,
        dataIdFromObject: ({ id }) => (id ? String(id) : undefined),
    }),
    typeDefs: LocalSchema,
    resolvers: LocalResolvers as any,
})

const ApolloWrapper: React.FC<ApolloWrapperProps> = ({ children }) => (
    <ApolloProvider client={apolloClient}>{children}</ApolloProvider>
)

export default ApolloWrapper
